import { z, ZodError } from "zod";

// We've defined here the validations & schema for making sure the env vars ARE correct.
const defaultValidation = z.string().min(1, "Env Var is not defined");
const numericValidation = defaultValidation.refine(
  (value) => !isNaN(parseFloat(value)),
  {
    message: "Env Var must be a number",
  },
);

const envSchema = z.object({
  VITE_APP_ENV: defaultValidation,
  VITE_APP_URL: defaultValidation,
  VITE_GOOGLE_AUTH_SSO_CLIENT_ID: defaultValidation,
  VITE_API_URL: defaultValidation,
  VITE_SENTRY_DSN: defaultValidation,
  VITE_SENTRY_TRACE_PROPAGATION_TARGET_REGEX: defaultValidation,
  VITE_AUDIO_MAX_DURATION: numericValidation,
  VITE_AUDIO_MIN_DURATION: numericValidation,
});

type EnvValues = z.infer<typeof envSchema>;

// IDEA: trigger a toast message on dev instead of just a console error
function logEnvError(errors: ZodError<EnvValues>) {
  const { properties } = z.treeifyError(errors);

  if (!properties || !Object.keys(properties).length) return;

  Object.entries(properties).forEach(([key, value]) => {
    if (value?.errors?.length) {
      console.error(`- ${key}: ${value.errors.join(", ")}`);
    }
  });
}

function checkEnv() {
  try {
    return envSchema.parse(import.meta.env);
  } catch (errors) {
    if (errors instanceof ZodError) {
      logEnvError(errors as ZodError<EnvValues>);
    }

    // So! We had some validation errors
    // let's just make sure the shape of the object is what we are expecting
    return Object.fromEntries(
      Object.keys(envSchema.shape).map((key) => [
        key,
        import.meta.env[key] ?? "",
      ]),
    ) as EnvValues;
  }
}

export const env = checkEnv();
